"use client";

import posthog from "posthog-js";
import { useEffect, useState } from "react";
import type { NoteCounters } from "@/lib/note-counters";

/**
 * The line under the dek: date · watch time · likes · questions asked · share.
 * Counts are real (lib/note-counters.ts). Views are counted on load but only
 * displayed once the API says showViews (a note past ~100 views).
 */
export function NoteMeta({
  slug,
  title,
  dateLabel,
  duration,
  url,
}: {
  slug: string;
  title: string;
  dateLabel: string;
  duration?: string;
  url: string;
}) {
  const [c, setC] = useState<NoteCounters | null>(null);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`/api/notes?slug=${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setC(d))
      .catch(() => {});
    // One view per browser session per note.
    try {
      const k = `kreayt-viewed-${slug}`;
      if (!sessionStorage.getItem(k)) {
        sessionStorage.setItem(k, "1");
        fetch("/api/notes", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ slug, action: "view" }),
        }).catch(() => {});
      }
      setLiked(localStorage.getItem(`kreayt-liked-${slug}`) === "1");
    } catch {}
  }, [slug]);

  const track = (event: string, extra: Record<string, unknown> = {}) => {
    if (posthog.__loaded) posthog.capture(event, { note_slug: slug, note_title: title, ...extra });
  };

  async function onLike() {
    if (liked) return;
    setLiked(true);
    try {
      localStorage.setItem(`kreayt-liked-${slug}`, "1");
    } catch {}
    track("note_like");
    const r = await fetch("/api/notes", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ slug, action: "like" }),
    }).catch(() => null);
    const d = r && r.ok ? await r.json().catch(() => null) : null;
    if (d) setC(d);
  }

  const share = (network: "linkedin" | "x") => {
    track("note_share", { network });
    const u = encodeURIComponent(url);
    const t = encodeURIComponent(title);
    const href =
      network === "linkedin"
        ? `https://www.linkedin.com/sharing/share-offsite/?url=${u}`
        : `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
    window.open(href, "_blank", "noopener,noreferrer,width=640,height=560");
  };

  async function copyLink() {
    track("note_share", { network: "copy" });
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  }

  const likes = c?.likes ?? 0;
  const asks = c?.asks ?? 0;

  return (
    <div className="notemeta">
      <span className="nm">{dateLabel}</span>
      {duration && (
        <>
          <span className="sep">·</span>
          <span className="nm">{duration} watch</span>
        </>
      )}
      {c?.showViews && (
        <>
          <span className="sep">·</span>
          <span className="nm">{c.views.toLocaleString()} views</span>
        </>
      )}
      <span className="sep">·</span>
      <button type="button" className={liked ? "nmbtn on" : "nmbtn"} onClick={onLike} aria-pressed={liked} aria-label="Like this note">
        <svg width="15" height="15" viewBox="0 0 24 24" fill={liked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
        </svg>
        <span>{likes}</span>
      </button>
      <span className="sep">·</span>
      <span className="nm nmq">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1.1-4.4A8 8 0 1 1 21 12z" />
        </svg>
        {asks} question{asks === 1 ? "" : "s"} asked
      </span>
      <span className="nmshare">
        <span className="nmlbl">Share</span>
        <button type="button" className="nmlink" onClick={() => share("linkedin")}>LinkedIn</button>
        <button type="button" className="nmlink" onClick={() => share("x")}>X</button>
        <button type="button" className="nmlink" onClick={copyLink}>{copied ? "Copied" : "Copy link"}</button>
      </span>
    </div>
  );
}
